// Rural Land section content — "Unlock Your Property's Potential"
// All legislative content reviewed July 2026 against NSW government sources.

export const RURAL_REVIEW_DATE = "July 2026";

// Matches backend OPPORTUNITY_TITLES in ruralroadmap.py — keep in sync.
export const OPPORTUNITY_TITLES_LIST = [
  "Farm Experiences & Agritourism",
  "Farm Stays & Visitor Accommodation",
  "Camping, Caravan & Hipcamp-Style Stays",
  "Secondary Dwellings & Additional Accommodation",
  "Farm Gate Sales & Roadside Stalls",
  "Agricultural Diversification & Value-Added Production",
  "Farm Buildings & Infrastructure",
  "Small-Scale Events, Workshops & Education",
  "Land Leasing & Agistment",
  "Conservation & Biodiversity Stewardship Income",
];

export const RURAL_INTRO = {
  eyebrow: "Rural Land",
  title: "Unlock Your Property's Potential",
  tagline: "More from your land. More possibilities for your future.",
  lead: "Your land could offer more than you realise.",
  body: [
    "Owning rural land comes with extraordinary opportunities — but understanding what you can legally do with that land isn't always straightforward.",
    "From hosting campers and creating farm experiences to selling produce, offering rural accommodation and exploring conservation opportunities, there may be ways to diversify your income without abandoning your agricultural operations.",
    "At PLAN FIRST NSW, we believe landowners deserve access to clear, practical information about what is possible. Our rural land resources help you explore new opportunities, understand NSW planning requirements and protect the very landscapes that make your property special.",
  ],
  closing: "Because knowing your land's potential should not require a planning degree.",
};

// Section 1 — the eight income opportunities
export const OPPORTUNITIES = [
  {
    slug: "farm-camping-hipcamp",
    title: "Farm Camping & Hipcamp",
    tagline: "Low-impact camping, self-contained caravans and campervans on working rural land.",
    involves: "Hosting paying campers in tents, campervans or self-contained caravans at designated sites on your property — from a single riverside clearing to several scattered sites. Platforms like Hipcamp, Youcamp-style listings and direct bookings connect you with guests.",
    income: "Nightly site fees, typically $30–$150+ per site per night depending on location, facilities and demand. Very low setup cost if you start with a simple, unpowered site.",
    agritourism: "Yes — camping is generally treated as farm stay accommodation under the NSW agritourism framework, provided it is ancillary to a commercial farm and the land is zoned RU1, RU2 or RU4. A limited exemption also allows up to two caravans or tents occupied for no more than two consecutive days, and no more than 60 days in any 12 months, without approval.",
    approvals: "Small-scale hosting may be exempt or complying development under the Codes SEPP if all standards are met; larger or permanent operations need a DA. Section 68 approvals under the Local Government Act 1993 can still apply to moveable dwellings and on-site sewage. Listing on Hipcamp is not development consent.",
    costs: "Low to moderate: site preparation, access maintenance, a composting toilet or amenities, signage, insurance and platform commissions.",
    environmental: "Bushfire and flood access and evacuation, waterways protection, waste management, and keeping campers clear of livestock and sensitive vegetation.",
    sources: [
      { label: "NSW Planning — Agritourism planning changes", url: "https://www.planning.nsw.gov.au/policy-and-legislation/under-review-and-new-policy-and-legislation/agritourism/agritourism-planning-changes" },
      { label: "Codes SEPP (full text)", url: "https://legislation.nsw.gov.au/view/whole/html/inforce/current/epi-2008-0572" },
      { label: "Hipcamp — become a host", url: "https://www.hipcamp.com/en-AU/host" },
    ],
  },
  {
    slug: "glamping-rural-accommodation",
    title: "Glamping & Rural Accommodation",
    tagline: "Farm stays, cabins, tiny-home-style stays and adapted farm buildings.",
    involves: "Offering guest accommodation — a converted shearers' quarters, a farm cottage, glamping tents with real beds, or purpose-built cabins — marketed as a rural escape.",
    income: "Nightly rates commonly $150–$500+ for quality rural stays, with weekend and holiday premiums. Higher setup cost than camping, but stronger returns per booking.",
    agritourism: "Yes — guest rooms, cottages and glamping fall under farm stay accommodation in the agritourism framework when ancillary to a commercial farm.",
    approvals: "Permanent structures, plumbing and building conversions almost always need a DA and Construction Certificate, plus BASIX. Some small-scale farm stay operations can meet exempt or complying standards — the detail matters.",
    costs: "Moderate to high: building works, fit-out, amenities, fire safety measures, insurance, cleaning and linen services.",
    environmental: "Effluent disposal, water supply, bushfire construction standards (BAL ratings may apply), and guest safety around farm operations.",
    sources: [
      { label: "NSW Planning — Agritourism planning changes", url: "https://www.planning.nsw.gov.au/policy-and-legislation/under-review-and-new-policy-and-legislation/agritourism/agritourism-planning-changes" },
      { label: "NSW Planning Portal Spatial Viewer", url: "https://www.planningportal.nsw.gov.au/spatialviewer" },
    ],
  },
  {
    slug: "farm-gate-sales",
    title: "Farm Gate Sales",
    tagline: "Produce stalls, farm shops, pick-your-own and tastings.",
    involves: "Selling what you grow or make directly to visitors — an honesty-box stall, a weekend farm shop, pick-your-own fruit, honey, preserves or small-batch tastings.",
    income: "Margin on your own produce without wholesalers; small stalls can earn hundreds per weekend, while established farm gates become destination businesses.",
    agritourism: "Yes — this is the farm gate premises category. Small-scale operations meeting the standards can be exempt development.",
    approvals: "Exempt standards include a maximum of 100 visitors at any one time (pick-your-own visitors are excluded from this cap), set trading hours, and setbacks of at least 50 metres from property boundaries and 250 metres from neighbouring dwellings. Food sales also require council food notification/licensing — separate from planning.",
    costs: "Low for a roadside stall; moderate for a shop fit-out, cool storage, signage and parking.",
    environmental: "Traffic and parking management, food safety, waste, and biosecurity where visitors enter production areas.",
    sources: [
      { label: "NSW Planning — Setting up an agritourism business", url: "https://www.planning.nsw.gov.au/sites/default/files/2023-09/setting-up-an-agritourism-business.pdf" },
      { label: "Codes SEPP (full text)", url: "https://legislation.nsw.gov.au/view/whole/html/inforce/current/epi-2008-0572" },
    ],
  },
  {
    slug: "farm-experiences",
    title: "Farm Experiences",
    tagline: "Tours, animal encounters, demonstrations and workshops.",
    involves: "Visitors paying to experience farm life — guided tours, feeding animals, shearing demonstrations, cheese-making or bread-baking workshops, school excursions and photography days.",
    income: "Per-person ticket pricing; experiences pair powerfully with farm gate sales and accommodation packages.",
    agritourism: "Yes — small-scale, low-impact visitor activities are the farm experience premises category.",
    approvals: "Low-impact experiences associated with a commercial farm can meet exempt or complying standards. Regular large groups, permanent structures or after-hours events push you toward a DA.",
    costs: "Low to moderate: public liability insurance, safe viewing areas, amenities and any equipment for workshops.",
    environmental: "Animal welfare and visitor safety, dust and noise, manure and runoff management, and biosecurity between visitor groups and livestock.",
    sources: [
      { label: "NSW Planning — Agritourism planning changes", url: "https://www.planning.nsw.gov.au/policy-and-legislation/under-review-and-new-policy-and-legislation/agritourism/agritourism-planning-changes" },
    ],
  },
  {
    slug: "rural-events-retreats",
    title: "Rural Events & Retreats",
    tagline: "Small functions, wellness retreats, workshops and weddings.",
    involves: "Hiring your property's scenery for private events — paddock weddings, yoga and wellness retreats, corporate farm days, long-table lunches and photography or film shoots.",
    income: "Venue hire fees from hundreds to many thousands per event; weddings and retreats are the premium end of rural income.",
    agritourism: "Partly — some small events can sit within farm experience premises, but a recurring wedding or function venue is usually a separate land use (function centre) outside the agritourism framework.",
    approvals: "Expect a DA for anything regular or large-scale. Traffic, noise, hours, amenities and liquor licensing are the usual assessment issues. One-off small gatherings may be treated differently — verify with council first.",
    costs: "Moderate to high: insurance, amenities, parking, wet-weather contingencies and event management time.",
    environmental: "Noise and light impacts on neighbours, traffic on rural roads, waste, and protecting waterways and vegetation from event footprints.",
    sources: [
      { label: "NSW Planning — Agritourism planning changes", url: "https://www.planning.nsw.gov.au/policy-and-legislation/under-review-and-new-policy-and-legislation/agritourism/agritourism-planning-changes" },
      { label: "NSW Planning Portal Spatial Viewer", url: "https://www.planningportal.nsw.gov.au/spatialviewer" },
    ],
  },
  {
    slug: "land-leasing-agistment",
    title: "Land Leasing & Agistment",
    tagline: "Grazing arrangements, agistment and leasing land to other producers.",
    involves: "Leasing paddocks for grazing (agistment), cropping licences, or longer-term leases to neighbouring farmers who need extra country — income without visitors on your land.",
    income: "Steady per-head, per-hectare or per-season payments; drought conditions can push agistment demand — and rates — up significantly.",
    agritourism: "No — this is an agricultural land management arrangement, not agritourism. It generally doesn't need planning approval where agriculture is already lawful, but the contract does the heavy lifting.",
    approvals: "Planning approval is rarely the issue. What matters is a written agreement covering stocking rates, duration, water access, fencing responsibility, weed control, biosecurity duties, animal welfare obligations and what happens in drought or fire.",
    costs: "Low: fencing repairs, water infrastructure and legal advice on the agreement are the main items.",
    environmental: "Stocking rates matched to pasture condition, weed and pest spread via incoming stock, and protecting riparian areas from overgrazing.",
    sources: [
      { label: "NSW DPI — Biosecurity", url: "https://www.dpi.nsw.gov.au/biosecurity" },
      { label: "Local Land Services", url: "https://www.lls.nsw.gov.au/" },
    ],
  },
  {
    slug: "conservation-biodiversity",
    title: "Nature & Conservation Income",
    tagline: "Biodiversity stewardship agreements, credits and nature-based experiences.",
    involves: "Being paid to protect and manage native vegetation — a Biodiversity Stewardship Agreement with the NSW Biodiversity Conservation Trust can generate biodiversity credits that are sold to developers who need offsets. Guided nature experiences can complement this.",
    income: "Credit sales can range from thousands to substantial sums depending on the ecological value of your land, plus annual management payments under some programs.",
    agritourism: "No — conservation agreements sit under the Biodiversity Conservation Act 2017, separate from the planning framework. Nature-based tours may intersect with farm experience premises.",
    approvals: "A stewardship agreement is a serious, usually permanent legal commitment registered on title. It restricts clearing and some land uses forever — get independent legal and financial advice before signing.",
    costs: "Ecological assessment fees, legal costs, and ongoing management obligations (weed and pest control, fencing) funded by the agreement's management fund.",
    environmental: "This is the point of the exercise — protecting koala habitat, corridors and threatened species while earning from that protection.",
    sources: [
      { label: "NSW Biodiversity Conservation Trust", url: "https://www.bct.nsw.gov.au/" },
      { label: "Biodiversity Offsets Scheme", url: "https://www.environment.nsw.gov.au/topics/animals-and-plants/biodiversity-offsets-scheme" },
    ],
  },
  {
    slug: "other-rural-ventures",
    title: "Other Rural Ventures",
    tagline: "Photography hire, commercial leases, renewables and more.",
    involves: "The long tail of rural income: location hire for photography and film, leasing sheds or yards to tradies, hosting telecommunications infrastructure, or leasing land for solar or wind generation.",
    income: "Highly variable — a telecom lease or renewable energy agreement can be transformative; location photography is occasional but effortless.",
    agritourism: "Usually not — most of these ventures fall outside the NSW agritourism planning framework and need their own planning and legal assessment. Never assume rural zoning makes them automatically permissible.",
    approvals: "Each use has its own definition and permissibility in your LEP. Renewable projects have dedicated state-level pathways. Always verify permissibility with council before signing any agreement.",
    costs: "Variable; legal review of lease terms is the one cost you should never skip.",
    environmental: "Depends entirely on the venture — construction impacts, visual impact and land rehabilitation clauses matter most.",
    sources: [
      { label: "NSW Planning Portal Spatial Viewer", url: "https://www.planningportal.nsw.gov.au/spatialviewer" },
      { label: "Revenue NSW — Land tax", url: "https://www.revenue.nsw.gov.au/taxes-duties-levies-royalties/land-tax" },
    ],
  },
];

// Section 2 — Understanding NSW Agritourism
export const AGRITOURISM = {
  title: "Understanding Agritourism Without the Planning Jargon",
  lead: "In December 2022, NSW changed its planning rules to help farmers diversify into visitor economies — and expanded the reforms in 2023. Here's what the framework actually says, in plain English.",
  framework: [
    {
      name: "Environmental Planning & Assessment Act 1979",
      plain: "The foundation law of all planning in NSW. Every approval pathway — exempt, complying or DA — exists because this Act creates it.",
    },
    {
      name: "Codes SEPP 2008",
      plain: "State Environmental Planning Policy (Exempt and Complying Development Codes) 2008. This is where the agritourism exempt and complying pathways live, with their development standards.",
    },
    {
      name: "Standard Instrument LEP definitions",
      plain: "The December 2022 reforms wrote 'agritourism' into the standard template every council LEP is built on — creating three defined uses: farm stay accommodation, farm gate premises and farm experience premises.",
    },
    {
      name: "Your council's LEP and DCP",
      plain: "Local Environmental Plans set zoning and permissibility; Development Control Plans add local detail. The state rules operate through these local instruments.",
    },
    {
      name: "Local Government Act 1993",
      plain: "Even when planning consent isn't needed, separate Section 68 approvals can still apply — for example installing moveable dwellings or managing on-site sewage.",
    },
  ],
  definitions: [
    {
      term: "Farm stay accommodation",
      def: "Tourist and visitor accommodation provided on a commercial farm as something secondary to the farm itself — camping, caravans, glamping, guest rooms or cottages.",
    },
    {
      term: "Farm gate premises",
      def: "Using part of a commercial farm to sell and serve the farm's produce to visitors — stalls, shops, tastings and pick-your-own.",
    },
    {
      term: "Farm experience premises",
      def: "Small-scale, low-impact visitor activities on a commercial farm — tours, demonstrations, workshops and animal experiences.",
    },
  ],
  commercialFarm: [
    "Everything hinges on the phrase commercial farm. The agritourism uses must be ancillary — secondary — to a genuine farming operation.",
    "A commercial farm generally means land rated as farmland by council, or a primary production business under the Income Tax Assessment Act 1997. A vacant bush block or a hobby farm with no commercial production may not qualify for the agritourism pathways — other options may still exist, but they need individual assessment.",
    "The framework recognises reality: a commercial farm temporarily affected by natural disaster — drought, bushfire or flood — can still qualify while it recovers, subject to the legal definitions.",
  ],
  pathways: [
    {
      name: "Exempt Development",
      plain: "No planning consent needed at all — but only if every requirement and standard in the Codes SEPP is met. Miss one standard and you're no longer exempt.",
    },
    {
      name: "Complying Development",
      plain: "A fast-tracked complying development certificate from council or a private certifier, issued against fixed standards — weeks, not months, when your proposal fits the template.",
    },
    {
      name: "Development Application",
      plain: "Full assessment by council (or another consent authority) with neighbour notification and merit consideration. Needed for larger, permanent or non-standard proposals.",
    },
  ],
  comparison: {
    title: "What you can do, what you need to check, and when you might need approval",
    rows: [
      {
        idea: "Two tents for weekend campers, a few times a year",
        check: "Stay within the limited exemption: no more than 2 caravans/tents, stays of up to 2 consecutive days, no more than 60 days per 12 months",
        approval: "Often no approval — Requires Verification with your council",
      },
      {
        idea: "Regular Hipcamp sites on a working cattle farm (RU1)",
        check: "Farm stay accommodation standards in the Codes SEPP; ancillary to the commercial farm; bushfire/flood constraints; s68 for toilets",
        approval: "Potentially exempt or complying — Requires Verification against each standard",
      },
      {
        idea: "Roadside stall selling your own honey and eggs",
        check: "Farm gate premises standards: visitor caps, trading hours, setbacks; council food notification",
        approval: "Potentially exempt — Requires Verification; food licence separate",
      },
      {
        idea: "Converting a shearers' quarters into paid guest rooms",
        check: "Building conversion, plumbing, BASIX, fire safety, access",
        approval: "DA and Construction Certificate almost always required",
      },
      {
        idea: "Monthly paddock weddings for 100+ guests",
        check: "Likely a 'function centre' land use, not agritourism; traffic, noise, hours, liquor",
        approval: "DA required — and it's a different assessment pathway",
      },
      {
        idea: "Weekend farm tours for small groups",
        check: "Farm experience premises standards; insurance; animal welfare",
        approval: "Potentially exempt or complying — Requires Verification",
      },
    ],
  },
  note: "These examples are educational illustrations, not legal conclusions for any specific property. Another approval, permit, licence or certification may still be necessary even where planning consent is not required.",
};

// Section 3 — Hipcamp guide
export const HIPCAMP = {
  title: "Your Land. Your Campsite. Your Opportunity.",
  lead: "A beginner's guide to hosting campers through Hipcamp — written for landowners who have never hosted anyone. Listing on Hipcamp is not development consent; the legal checks come first.",
  hostUrl: "https://www.hipcamp.com/en-AU/host",
  steps: [
    {
      n: 1,
      title: "Check whether you can legally host campers",
      points: [
        "Find your zoning in the NSW Planning Portal Spatial Viewer or your council's LEP maps — agritourism pathways generally apply to RU1, RU2 and RU4 zones.",
        "Confirm your property is a commercial farm (rated farmland or a primary production business) — farm stay accommodation must be ancillary to it.",
        "Check which pathway applies: the limited casual-camping exemption, exempt or complying development under the Codes SEPP, or a DA.",
        "Confirm permitted guest numbers, stay durations and the development standards for your pathway.",
        "Check setbacks from boundaries, neighbouring dwellings, waterways and neighbouring agricultural activities.",
        "Identify bushfire, flooding and environmental constraints on the sites you're considering.",
        "Ask council about Section 68 requirements for caravans, tents and other moveable dwellings, and on-site sewage.",
        "Remember: a Hipcamp listing is a marketing channel, not an approval.",
      ],
    },
    {
      n: 2,
      title: "Prepare your property",
      points: [
        "Safe road access and clearly marked parking.",
        "All-weather access suitable for emergency vehicles.",
        "Safe, suitable camping areas away from hazards.",
        "Toilet and waste management arrangements.",
        "Drinking water availability — or clear disclosure that guests must bring their own.",
        "Fire management plan and emergency evacuation route.",
        "Flood and severe weather risk procedures.",
        "Privacy buffers for neighbouring properties.",
        "Fencing and livestock exclusion from camping areas.",
        "Weed and pest management around sites.",
        "Protection of native vegetation and habitat.",
        "Signage marking permitted and restricted areas.",
        "Appropriate public liability insurance.",
        "Any necessary development or other approvals in hand.",
      ],
    },
    {
      n: 3,
      title: "Register and create your listing",
      points: [
        "Visit hipcamp.com/en-AU/host and start the host registration.",
        "Prepare your property description and genuine, well-lit photographs.",
        "Define your campsite types (tent, campervan, caravan) and maximum guest and vehicle numbers.",
        "Be accurate about amenities and toilet arrangements — Hipcamp has host standards and a toilet policy you must meet.",
        "Write clear access directions, check-in and check-out arrangements.",
        "Set your pets and campfire policies, house rules and guest expectations.",
        "Nominate environmental protection requirements for guests.",
        "Set pricing and availability — you can adjust both any time.",
      ],
    },
    {
      n: 4,
      title: "Understand your financial responsibilities",
      points: [
        "Gross booking revenue is not income: platform commissions come off the top of every booking.",
        "Hosting income is generally assessable for tax — keep records from day one and talk to your accountant.",
        "Budget for insurance, maintenance, sanitation supplies and your own time.",
        "Warning: earning non-agricultural income from land can affect primary production land tax exemptions — check with Revenue NSW before you start.",
      ],
    },
    {
      n: 5,
      title: "Manage your guests responsibly",
      points: [
        "Communicate before arrival: directions, rules, what to bring.",
        "Prepare sites before each booking and inspect after departure.",
        "Notify guests of fire bans, weather warnings or emergencies promptly.",
        "Have an incident process: who to call, where the assembly point is.",
        "Send every guest a pre-arrival information pack — use our Guest Information Pack Generator to build yours in minutes.",
      ],
    },
  ],
};

// Section 4 — Protect your land
export const PROTECT = {
  title: "Earn From Your Land. Care for What Makes It Special.",
  lead: "Responsible land management isn't the price of rural tourism — it's the product. Guests come for healthy landscapes, clear waterways and thriving wildlife. Protecting them protects your income.",
  closing: "Guests have responsibilities, and a guest agreement helps set expectations — but it never removes your legal responsibilities as the landholder.",
  topics: [
    {
      title: "Bushfire",
      points: [
        "Check fire danger ratings and total fire bans daily in fire season — and build cancellation procedures around them.",
        "Set campfire rules that follow RFS restrictions, with safe cooking alternatives ready.",
        "Plan evacuation routes and assembly points, and keep emergency access clear.",
      ],
    },
    {
      title: "Flooding",
      points: [
        "Know which parts of your property are flood-prone or subject to overland flow — and never site campers there.",
        "Monitor NSW SES flood warnings and have closure and evacuation procedures ready.",
        "Remember that waterways and access roads change after floods — re-inspect before rebooking.",
      ],
    },
    {
      title: "Koala habitat & native vegetation",
      points: [
        "Identify koala food trees, hollow-bearing trees and habitat corridors before choosing sites.",
        "Clearing restrictions apply even on private land — campsite 'improvements' can breach them.",
        "Keep campsites and traffic away from sensitive habitat; disturbed wildlife doesn't return quickly.",
      ],
    },
    {
      title: "Waterways & pollution",
      points: [
        "Set rules for litter, greywater and human waste — and provide the facilities to make them easy to follow.",
        "Keep chemicals and fuel away from creeks, rivers, wetlands and dams.",
        "Manage sediment and erosion around sites and access tracks, especially after rain.",
      ],
    },
    {
      title: "Biosecurity",
      points: [
        "Visitor vehicles, footwear and equipment carry seeds and disease — set simple hygiene expectations.",
        "Keep guests and pets out of livestock areas unless you're managing the interaction.",
        "Know your obligations if you suspect a pest or disease incursion.",
      ],
    },
    {
      title: "Neighbouring properties",
      points: [
        "Noise, smoke, headlights and traffic travel further in the country — site camps with neighbours in mind.",
        "Maintain boundary fencing and make sure guests understand a working farm continues around them.",
        "Tell neighbours before your first booking arrives — goodwill is cheaper than complaints.",
      ],
    },
  ],
};

// Section 5 Tool E — Resource hub
export const RESOURCES = [
  {
    group: "Safety & emergencies",
    note: "We link to official sources rather than reproducing live warnings — always check current conditions directly.",
    links: [
      { label: "NSW Rural Fire Service — fire danger ratings & total fire bans", url: "https://www.rfs.nsw.gov.au/fire-information/fdr-and-tobans" },
      { label: "NSW SES — flood and storm warnings", url: "https://www.ses.nsw.gov.au/" },
      { label: "Hazards Near Me NSW (official app)", url: "https://www.nsw.gov.au/emergency/hazards-near-me" },
    ],
  },
  {
    group: "Planning & property research",
    links: [
      { label: "NSW Planning Portal Spatial Viewer", url: "https://www.planningportal.nsw.gov.au/spatialviewer" },
      { label: "NSW Government — agritourism planning guidance", url: "https://www.planning.nsw.gov.au/policy-and-legislation/under-review-and-new-policy-and-legislation/agritourism/agritourism-planning-changes" },
      { label: "Codes SEPP — full legislation text", url: "https://legislation.nsw.gov.au/view/whole/html/inforce/current/epi-2008-0572" },
      { label: "Find your local council", url: "https://www.service.nsw.gov.au/transaction/find-your-local-council" },
    ],
  },
  {
    group: "Hosting platforms",
    links: [
      { label: "Hipcamp — become a host", url: "https://www.hipcamp.com/en-AU/host" },
      { label: "Hipcamp — host help centre", url: "https://support.hipcamp.com/hc/en-us" },
    ],
  },
  {
    group: "Land, environment & tax",
    links: [
      { label: "NSW DPI — farm biosecurity", url: "https://www.dpi.nsw.gov.au/biosecurity" },
      { label: "NSW Biodiversity Conservation Trust", url: "https://www.bct.nsw.gov.au/" },
      { label: "Koala habitat information (DCCEEW)", url: "https://www.environment.nsw.gov.au/topics/animals-and-plants/threatened-species/nsw-threatened-species-program/koala" },
      { label: "Revenue NSW — land tax and exemptions", url: "https://www.revenue.nsw.gov.au/taxes-duties-levies-royalties/land-tax" },
    ],
  },
];

// Tool B — Hipcamp readiness checklist
export const CHECKLIST = [
  {
    group: "Planning & legal",
    items: [
      { text: "Confirmed zoning and that farm stay provisions can apply", mandatory: true },
      { text: "Confirmed the property qualifies as a commercial farm (or verified the alternative pathway)", mandatory: true },
      { text: "Checked which development pathway applies (exemption / exempt / complying / DA)", mandatory: true },
      { text: "Obtained any required development consent before listing", mandatory: true },
      { text: "Checked Section 68 requirements for moveable dwellings and sewage", mandatory: true },
      { text: "Checked land tax implications with Revenue NSW or your accountant", mandatory: false },
    ],
  },
  {
    group: "Safety",
    items: [
      { text: "All-weather access suitable for emergency vehicles", mandatory: true },
      { text: "Emergency evacuation route and assembly point identified", mandatory: true },
      { text: "Fire management plan and guest fire rules in place", mandatory: true },
      { text: "Flood and severe weather closure procedure written", mandatory: true },
      { text: "First aid kit and emergency contacts list on site", mandatory: false },
    ],
  },
  {
    group: "Environmental protection",
    items: [
      { text: "Campsites located away from waterways, habitat and sensitive vegetation", mandatory: true },
      { text: "Waste, litter and greywater arrangements in place", mandatory: true },
      { text: "Biosecurity expectations set for vehicles, footwear and pets", mandatory: false },
      { text: "Restricted areas clearly signed", mandatory: false },
    ],
  },
  {
    group: "Sanitation & amenities",
    items: [
      { text: "Toilet arrangements meet Hipcamp host standards and council requirements", mandatory: true },
      { text: "Drinking water available — or non-availability clearly disclosed", mandatory: true },
      { text: "Rubbish removal process in place", mandatory: false },
    ],
  },
  {
    group: "Business readiness",
    items: [
      { text: "Public liability insurance covering paying guests", mandatory: true },
      { text: "Pricing researched against comparable local sites", mandatory: false },
      { text: "Tax record-keeping set up with your accountant", mandatory: false },
      { text: "House rules, pet and campfire policies written", mandatory: false },
    ],
  },
  {
    group: "Guest management",
    items: [
      { text: "Pre-arrival information pack created (use our Guest Pack Generator)", mandatory: false },
      { text: "Check-in/check-out process defined", mandatory: false },
      { text: "Post-stay site inspection routine established", mandatory: false },
      { text: "Incident and complaint process agreed", mandatory: false },
    ],
  },
];

// Section 2 — summary table + featured example
export const CATEGORY_TABLE = [
  { term: "Farm stay accommodation", opportunity: "Farm camping, caravan sites, glamping, cabins and certain existing accommodation" },
  { term: "Farm gate premises", opportunity: "Produce sales, tastings, farm shops, pick-your-own experiences, workshops and cafés" },
  { term: "Farm experience premises", opportunity: "Farm tours, educational visits, horse riding, small functions, retreats and weddings" },
];

export const FEATURED_EXAMPLE = {
  title: "A real example, straight from NSW Planning",
  intro: "NSW Planning explains that qualifying commercial farms may accommodate campers as exempt development where all relevant standards are satisfied. Published requirements include:",
  standards: [
    "A minimum landholding of 15 hectares.",
    "No more than 20 camping guests at any one time.",
    "A maximum stay of 21 days per visit.",
    "Additional requirements governing campsite locations, separation distances, vehicles, access, amenities and environmental hazards.",
  ],
  caution: "These are not blanket permissions to operate a campground — every standard must be met, and separate council approvals may still be necessary under the Local Government Act 1993.",
  source: { label: "NSW Planning — Agritourism & small-scale agriculture FAQ", url: "https://www.planning.nsw.gov.au/sites/default/files/2023-03/agritourism-and-small-scale-agriculture-development-faq.pdf" },
};

// Tool D — Guest Information Pack template (fixed sections; form supplies the bracketed fields)
export const GUEST_PACK_FIXED = [
  {
    heading: "A Little About Our Land",
    body: [
      "We're delighted to welcome you to our property.",
      "Our land is not just somewhere to stay. It's a place we care for, work on and hope to preserve for generations to come.",
      "By staying with us, you're helping support a rural landowner and the local community.",
      "We ask that you enjoy your experience while treating the land, its wildlife and our neighbours with consideration and respect.",
    ],
  },
  {
    heading: "Before You Arrive",
    body: [
      "Please familiarise yourself with the property access instructions and any safety warnings we've provided.",
      "Check current weather conditions, local fire restrictions and emergency warnings.",
      "Make sure you're bringing the equipment, drinking water and toilet facilities specified in your booking.",
      "Please contact us before arrival if you have any questions or need clarification about access.",
    ],
  },
  {
    heading: "Fire Safety",
    body: [
      "Campfires are only permitted when the host authorises them and applicable fire restrictions allow them.",
      "Check current NSW Rural Fire Service fire restrictions. Never light a campfire during a Total Fire Ban.",
      "Follow all additional property-specific fire restrictions. Use approved fire areas only, when fires are permitted.",
      "Never leave a permitted fire unattended. Do not collect fallen timber or cut vegetation for firewood.",
      "Follow host instructions regarding evacuation or site closure.",
      "Official information: rfs.nsw.gov.au/fire-information/fdr-and-tobans",
    ],
  },
  {
    heading: "Protecting Our Wildlife and Vegetation",
    body: [
      "Our property may support native wildlife, including koalas, birds and other protected species.",
      "Please stay within designated areas. Do not remove, damage or cut vegetation, including eucalyptus trees, hollow-bearing trees, fallen timber or native plants.",
      "Do not disturb wildlife, nests or habitat areas. Avoid unnecessary lighting and noise near wildlife habitat.",
      "Respect all signs marking restricted or environmentally sensitive locations.",
    ],
  },
  {
    heading: "Waste and Pollution",
    body: [
      "Our expectation is simple: please leave the property as you found it, or better.",
      "Take all rubbish, recycling, cigarette butts and personal items with you unless an approved disposal facility is provided.",
      "Use only the toilet and wastewater arrangements identified by the host. Never empty portable toilets or wastewater onto the ground, into a waterway or into an unapproved facility.",
      "Never dispose of chemicals, detergents, fuel or other pollutants on the property. Report any accidental spills or pollution incidents to your host immediately.",
    ],
  },
  {
    heading: "Waterways, Creeks and Dams",
    body: [
      "Waterways are important parts of our landscape and local ecosystems.",
      "Do not drive through creek beds or across sensitive ground unless specifically directed to an authorised crossing.",
      "Respect any swimming, fishing or water access restrictions. Do not use soaps, detergents or washing products in natural waterways.",
    ],
  },
  {
    heading: "Respecting Our Working Property",
    body: [
      "Please close gates as instructed, respect fencing and remain away from livestock, machinery and operational areas unless authorised.",
      "Do not feed animals without permission.",
      "Follow any biosecurity instructions concerning footwear, vehicles, pets or equipment — help us prevent weeds, pests and diseases spreading between properties.",
    ],
  },
  {
    heading: "Severe Weather and Emergencies",
    body: [
      "Your safety matters. Weather and property conditions can change quickly — your host may issue additional instructions or close the property when conditions are unsafe.",
      "Do not attempt to cross floodwater. In an emergency, call 000.",
      "NSW SES flood information: ses.nsw.gov.au/during-emergency/during-flood",
    ],
  },
  {
    heading: "Before You Leave",
    body: [
      "Please check that all personal belongings and rubbish have been removed.",
      "Toilet and wastewater arrangements have been managed correctly.",
      "Campfires are fully extinguished, where fires were authorised.",
      "Gates and access areas have been left as instructed.",
      "No damage, pollution or hazards have been left unreported.",
      "Your campsite is clean and ready for the next visitor.",
    ],
  },
  {
    heading: "Thank You for Caring for Our Land",
    body: [
      "Your booking makes a difference to a rural property owner.",
      "Your consideration helps us continue welcoming visitors while looking after the land we love.",
      "Thank you for supporting responsible rural tourism.",
    ],
  },
];

export const GUEST_PACK_ACKNOWLEDGEMENT =
  "I confirm that I have read the property information, understand the host's rules and will follow applicable safety and environmental instructions.";

export const GUEST_PACK_DISCLAIMER =
  "This information pack supplements, but does not replace, applicable laws, emergency plans, booking terms or the host's legal responsibilities.";

export const RURAL_BRAND_LINE =
  "PLAN FIRST isn't teaching people to exploit their land. It helps you understand its possibilities, diversify your income and remain a responsible custodian of it.";

export const RURAL_CTAS = [
  { label: "Explore My Land's Potential", to: "/rural/explorer", testid: "rural-cta-explorer" },
  { label: "Understand Agritourism", to: "/rural/agritourism", testid: "rural-cta-agritourism" },
  { label: "Can I Host Campers?", to: "/rural/hipcamp", testid: "rural-cta-hipcamp" },
  { label: "Download My Hosting Checklist", to: "/rural/checklist", testid: "rural-cta-checklist" },
  { label: "Create My Guest Information Pack", to: "/rural/guest-pack", testid: "rural-cta-guestpack" },
  { label: "Planner Connect — coming soon", to: "/book", testid: "rural-cta-book" },
];
